
import { supabase } from "@/integrations/supabase/client";
import { AssessmentResponse } from "@/types/assessment";

export interface ResultsResponse {
  id: string;
  assessmentName: string;
  respondentName: string;
  respondentEmail: string;
  company: string;
  submittedAt: string;
  averageScore: number;
  assessmentId: string;
}

export async function getAllResponses(): Promise<ResultsResponse[]> {
  try {
    const { data, error } = await supabase
      .from("responses")
      .select(`
        *,
        assessments!inner(title)
      `)
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error("Error fetching all responses:", error);
      throw error;
    }

    // Transform database responses to UI format
    return data.map(rawResponse => {
      // Parse JSON fields safely
      const personalInfo = typeof rawResponse.personal_info === 'string' 
        ? JSON.parse(rawResponse.personal_info as string) 
        : rawResponse.personal_info;
      
      const scoresByCategory = typeof rawResponse.scores_by_category === 'string'
        ? JSON.parse(rawResponse.scores_by_category as string)
        : rawResponse.scores_by_category;

      // Calculate average score from scores by category
      const scores = Object.values(scoresByCategory || {}) as Array<{current: number}>;
      const avgScore = scores.length > 0 
        ? scores.reduce((sum, score) => sum + (score.current || 0), 0) / scores.length 
        : 0;

      return {
        id: rawResponse.id,
        assessmentId: rawResponse.assessment_id,
        assessmentName: (rawResponse.assessments as any)?.title || 'Unknown Assessment',
        respondentName: personalInfo?.name || personalInfo?.fullName || 'Anonymous',
        respondentEmail: personalInfo?.email || '',
        company: personalInfo?.company || '',
        submittedAt: rawResponse.submitted_at,
        averageScore: Number(avgScore.toFixed(1))
      };
    });
  } catch (error) {
    console.error("Error in getAllResponses:", error);
    throw error;
  }
}

export async function getUniqueAssessmentNames(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from("assessments")
      .select("title")
      .eq("is_active", true);

    if (error) {
      console.error("Error fetching assessment names:", error);
      throw error;
    }

    return data.map(assessment => assessment.title);
  } catch (error) {
    console.error("Error in getUniqueAssessmentNames:", error);
    return [];
  }
}
